import { Link } from "react-router-dom";
import { Sparkles, ArrowRight, Check, Wand2, Layers, Zap, ShieldCheck, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import LanguageSwitcher from "../components/LanguageSwitcher";
import ExampleGallery from "../components/ExampleGallery";
import PaymentIcons from "../components/PaymentIcons";
import LibraryShowcase from "../components/LibraryShowcase";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/d71174fa-7b21-4a1b-abe7-f0b7113894a9/images/94518f362760a5628f0f87bab72e72659bf0e3d7dcf86009445b6528a871582c.jpeg";
const ANVIL_CREAM = "https://static.prod-images.emergentagent.com/jobs/d71174fa-7b21-4a1b-abe7-f0b7113894a9/images/9b5c03e8a2b89cf751d8c58fbc007515139b62dd6048517a120ced93b1b85d6c.jpeg";
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/d71174fa-7b21-4a1b-abe7-f0b7113894a9/images/0d32058d19421fb1553278ef18431e3323b41440572dc7bab66982025eec315e.jpeg";
const HOW_BG = "https://static.prod-images.emergentagent.com/jobs/d71174fa-7b21-4a1b-abe7-f0b7113894a9/images/a1a8620d5ca12f59c666f2d4e9fae4afc0b00bce03da426442be9b9f0e89ab88.jpeg";
const ABSTRACT_ORANGE = "https://images.unsplash.com/photo-1666827717869-448a0adcd4ac?crop=entropy&cs=srgb&fm=jpg&w=900&q=80";
const STUDIO_WARM = "https://images.unsplash.com/photo-1755520795091-adf1f3f307e0?crop=entropy&cs=srgb&fm=jpg&w=900&q=80";
const MODELS_LOGOS = [
  "GPT Image 2", "Nano Banana Pro", "Midjourney", "FLUX.2", "Ideogram", "Recraft",
  "Seedance", "Kling", "Veo", "Runway", "Luma",
];

const FAQ_KEYS = ["what", "account", "cancel", "annual", "which_models"];
const FAQ = {
  en: {
    what: ["What is Vixelforge?", "A SaaS tool that turns a short description into optimized, copy-ready prompts for AI image and video models."],
    account: ["Do I need an account to start?", "Yes, but registration is free — you pay only when you activate a plan (Basic from €6/month)."],
    cancel: ["Can I cancel anytime?", "Of course. Manage your subscription through the Stripe portal accessible from your Account page."],
    annual: ["How does annual billing work?", "With annual billing you save 30% compared to the equivalent monthly plan."],
    which_models: ["Which models are supported?", "Images: GPT Image 2, Nano Banana Pro, Midjourney, FLUX.2, Ideogram, Recraft. Video: Seedance, Kling, Veo, Runway, Luma."],
  },
  it: {
    what: ["Cos'e' Vixelforge?", "Uno strumento SaaS che trasforma una breve descrizione in prompt ottimizzati per modelli AI di immagini e video, pronti da copiare."],
    account: ["Ho bisogno di un account per iniziare?", "Si', la registrazione e' gratuita — paghi solo quando attivi un piano (Basic da 6 €/mese)."],
    cancel: ["Posso disdire quando voglio?", "Certo, gestisci l'abbonamento dal portale Stripe accessibile dalla pagina Account."],
    annual: ["Come funziona il pagamento annuale?", "Con la fatturazione annuale risparmi il 30% rispetto al totale mensile."],
    which_models: ["Quali modelli sono supportati?", "Immagini: GPT Image 2, Nano Banana Pro, Midjourney, FLUX.2, Ideogram, Recraft. Video: Seedance, Kling, Veo, Runway, Luma."],
  },
  es: {
    what: ["¿Qué es Vixelforge?", "Una herramienta SaaS que convierte una breve descripción en prompts optimizados para modelos IA de imagen y video."],
    account: ["¿Necesito una cuenta para empezar?", "Sí, el registro es gratuito — pagas solo cuando activas un plan (Basic desde 6 €/mes)."],
    cancel: ["¿Puedo cancelar cuando quiera?", "Por supuesto. Gestiona tu suscripción desde el portal Stripe en la página de Cuenta."],
    annual: ["¿Cómo funciona la facturación anual?", "Con facturación anual ahorras el 30% respecto al mensual."],
    which_models: ["¿Qué modelos son compatibles?", "Imágenes: GPT Image 2, Nano Banana Pro, Midjourney, FLUX.2, Ideogram, Recraft. Video: Seedance, Kling, Veo, Runway, Luma."],
  },
  fr: {
    what: ["Qu'est-ce que Vixelforge ?", "Un outil SaaS qui transforme une brève description en prompts optimisés pour les modèles IA d'image et vidéo."],
    account: ["Ai-je besoin d'un compte ?", "Oui, l'inscription est gratuite — vous payez uniquement en activant un plan (Basic dès 6 €/mois)."],
    cancel: ["Puis-je annuler à tout moment ?", "Bien sûr. Gérez votre abonnement via le portail Stripe accessible depuis la page Compte."],
    annual: ["Comment fonctionne la facturation annuelle ?", "Avec la facturation annuelle, économisez 30% par rapport au mensuel."],
    which_models: ["Quels modèles sont supportés ?", "Images : GPT Image 2, Nano Banana Pro, Midjourney, FLUX.2, Ideogram, Recraft. Vidéo : Seedance, Kling, Veo, Runway, Luma."],
  },
  de: {
    what: ["Was ist Vixelforge?", "Ein SaaS-Tool, das eine kurze Beschreibung in optimierte, kopierfertige Prompts für KI-Bild- und Videomodelle verwandelt."],
    account: ["Brauche ich ein Konto zum Starten?", "Ja, die Registrierung ist kostenlos — du zahlst erst mit Aktivierung eines Plans (Basic ab 6 €/Monat)."],
    cancel: ["Kann ich jederzeit kündigen?", "Natürlich. Verwalte dein Abo über das Stripe-Portal auf der Konto-Seite."],
    annual: ["Wie funktioniert die Jahreszahlung?", "Mit jährlicher Zahlung sparst du 30% gegenüber der monatlichen."],
    which_models: ["Welche Modelle werden unterstützt?", "Bilder: GPT Image 2, Nano Banana Pro, Midjourney, FLUX.2, Ideogram, Recraft. Video: Seedance, Kling, Veo, Runway, Luma."],
  },
};

export default function Landing() {
  const { t, lang } = useI18n();
  const { user, logout } = useAuth();
  const [plans, setPlans] = useState(null);
  const [billing, setBilling] = useState("yearly");
  useEffect(() => { api.get("/plans", { params: { lang } }).then((r) => setPlans(r.data)); }, [lang]);

  const faqDict = FAQ[lang] || FAQ.en;

  return (
    <div className="min-h-screen bg-[#0E0E12] text-white overflow-x-hidden">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0E0E12]/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
            <img src="/logo-mark.png" alt="Vixelforge" className="w-8 h-8 shrink-0" />
            <span className="font-black text-xl tracking-tight">Vixelforge</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#how" className="hover:text-white transition-colors">{t("nav_how")}</a>
            <a href="#models" className="hover:text-white transition-colors">{t("nav_models")}</a>
            <a href="#pricing" className="hover:text-white transition-colors">{t("nav_pricing")}</a>
            <a href="#faq" className="hover:text-white transition-colors">{t("nav_faq")}</a>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            {user ? (
              <>
                <Link to="/app"><Button data-testid="nav-open-app-btn" className="bg-orange-500 hover:bg-orange-600 text-black font-semibold rounded-full">{t("open_app") || "Open App"}</Button></Link>
                <Button
                  variant="ghost"
                  onClick={async () => { await logout(); }}
                  data-testid="nav-logout-btn"
                  className="text-zinc-300 hover:text-white hover:bg-white/5"
                >
                  {t("logout")}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" data-testid="nav-login-btn" className="text-zinc-300 hover:text-white hover:bg-white/5">{t("login")}</Button></Link>
                <Link to="/signup"><Button data-testid="nav-signup-btn" className="bg-orange-500 hover:bg-orange-600 text-black font-semibold rounded-full">{t("try_free")}</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="hidden lg:block absolute inset-y-0 right-0 w-[68%] pointer-events-none" aria-hidden="true">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover object-center" data-testid="hero-bg-image" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0E0E12] via-[#0E0E12]/70 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#0E0E12] to-transparent" />
          <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#0E0E12] to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center relative px-6 py-24 md:py-32 lg:min-h-[720px]">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-orange-500/30 bg-[#0E0E12]/60 backdrop-blur text-orange-400 text-xs uppercase tracking-widest font-bold mb-8">
              <Sparkles className="w-3 h-3" /> {t("badge_hero")}
            </div>
            <h1 className="font-black tracking-tight text-5xl md:text-6xl lg:text-7xl leading-[0.95] mb-6 [text-shadow:0_2px_24px_rgba(0,0,0,0.6)]">
              {t("tagline_1")}<br/>
              <span className="text-orange-400">{t("tagline_2")}</span>{t("tagline_3")}
            </h1>
            <p className="text-lg md:text-xl text-zinc-300 max-w-xl leading-relaxed mb-8">
              {t("hero_sub")}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={user ? "/app" : "/signup"}>
                <Button size="lg" data-testid="hero-cta-btn" className="bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-full px-8 h-12 shadow-[0_0_40px_-10px_rgba(249,115,22,0.6)]">
                  {user ? (t("open_app") || "Open App") : t("try_free")} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <a href="#how">
                <Button size="lg" variant="outline" className="rounded-full px-8 h-12 border-white/20 bg-[#0E0E12]/40 backdrop-blur hover:bg-white/5 text-white">
                  {t("learn_how")}
                </Button>
              </a>
            </div>
            {!user && (
              <div className="mt-3 text-xs text-zinc-400 flex flex-wrap items-center gap-2" data-testid="hero-microcopy">
                <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-400" /> {t("hero_micro_free") || "Free"}</span>
                <span>·</span>
                <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-400" /> {t("hero_micro_nocard") || "No card required"}</span>
                <span>·</span>
                <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-400" /> {t("hero_micro_gift") || "6 credits included"}</span>
              </div>
            )}
          </div>
          <div className="lg:hidden relative">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-[0_8px_48px_rgba(249,115,22,0.15)]">
              <img src={HERO_IMG} alt="Vixelforge forge" className="w-full h-[420px] object-cover" data-testid="hero-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E12] via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 rounded-xl border border-white/10 bg-[#0E0E12]/80 backdrop-blur-md p-4">
                <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-2">Nano Banana Pro · Output</div>
                <p className="text-xs text-zinc-200 leading-relaxed font-mono line-clamp-3">
                  A chrome megaphone erupting with molten orange light and floating crystal shards, dark cinematic product render. <span className="text-orange-400">Aspect ratio 2:3.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="relative overflow-hidden bg-[#F4EFEB] text-[#14141A]" data-testid="how-section">
        <div className="hidden lg:block absolute inset-y-0 left-0 w-[56%] pointer-events-none" aria-hidden="true">
          <img src={HOW_BG} alt="" className="w-full h-full object-cover object-left" data-testid="how-bg-image" />
          <div className="absolute inset-0 bg-[linear-gradient(to_left,#F4EFEB_0%,#F4EFEB_18%,rgba(244,239,235,0.75)_38%,rgba(244,239,235,0.15)_70%,transparent_100%)]" />
          <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#F4EFEB] to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[#F4EFEB] to-transparent" />
        </div>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-14 items-center relative px-6 py-24 lg:min-h-[760px]">
          <div className="lg:hidden">
            <img src={ANVIL_CREAM} alt="Forge anvil" className="w-full max-w-md mx-auto rounded-2xl" data-testid="how-image" />
          </div>
          <div className="lg:col-start-7 lg:col-span-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-orange-600 font-bold mb-3">{t("how_label")}</div>
            <h2 className="font-black text-4xl md:text-5xl tracking-tight max-w-2xl mb-10 text-[#14141A]">{t("how_title")}</h2>
            <div className="space-y-4">
              {[
                { icon: Wand2, tk: "step1_t", dk: "step1_d" },
                { icon: Layers, tk: "step2_t", dk: "step2_d" },
                { icon: Zap, tk: "step3_t", dk: "step3_d" },
                { icon: RefreshCw, tk: "step4_t", dk: "step4_d" },
              ].map((s, i) => (
                <div key={i} className="flex items-start gap-4 rounded-xl border border-[#D6CFC4] bg-[#EAE3D9]/85 backdrop-blur-sm p-5 hover:border-orange-500/50 transition-colors duration-200" data-testid={`step-${i+1}`}>
                  <div className="w-11 h-11 rounded-lg bg-[#14141A] flex items-center justify-center flex-shrink-0">
                    <s.icon className="w-5 h-5 text-orange-400" strokeWidth={1.8} />
                  </div>
                  <div>
                    <h3 className="font-black text-lg mb-1 tracking-tight text-[#14141A]">{t(s.tk)}</h3>
                    <p className="text-sm text-[#524E48] leading-relaxed">{t(s.dk)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <LibraryShowcase lang={lang} />

      <section id="models" className="py-16 px-6 bg-[#14141A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-6 text-center">{t("supported_models")}</div>
          <div className="flex flex-wrap justify-center gap-3">
            {MODELS_LOGOS.map((m) => (
              <div key={m} className="px-4 py-2 rounded-full border border-white/10 bg-white/[0.03] text-sm text-zinc-300" data-testid={`model-badge-${m.toLowerCase().replace(/[^a-z0-9]/g,"-")}`}>
                {m}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ExampleGallery />

      <section id="pricing" className="py-24 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 text-center">
            <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-3">{t("pricing_label")}</div>
            <h2 className="font-black text-4xl md:text-5xl tracking-tight mb-4">{t("pricing_title")}</h2>
            <p className="text-zinc-400">{t("pricing_sub")}</p>
            <div className="inline-flex mt-6 rounded-full border border-white/10 p-1 bg-[#121218]">
              {["monthly", "yearly"].map((b) => (
                <button
                  key={b}
                  onClick={() => setBilling(b)}
                  data-testid={`billing-toggle-${b}`}
                  className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-colors duration-200 ${
                    billing === b ? "bg-orange-500 text-black" : "text-zinc-400 hover:text-white"
                  }`}
                >
                  {b === "monthly" ? t("monthly") : t("yearly")}
                </button>
              ))}
            </div>
          </div>
          {plans && (
            <>
              {/* Free trial card (top row, no card required) */}
              <div className="mb-6 rounded-lg border border-emerald-500/30 bg-emerald-500/[0.04] p-6 flex items-center justify-between gap-6 flex-wrap" data-testid="plan-card-free">
                <div className="flex items-start gap-4 flex-1 min-w-[260px]">
                  <div className="w-11 h-11 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Sparkles className="w-5 h-5 text-emerald-300" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-3 flex-wrap mb-2">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-300">{t("free_plan_label") || "Free trial"}</div>
                      <div className="font-black text-2xl tracking-tight text-white">0€</div>
                      <div className="text-xs text-zinc-400">· {t("free_plan_no_card") || "No card required"}</div>
                    </div>
                    <ul className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-zinc-300">
                      <li className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" /> {t("free_plan_ai") || "6 free credits"}</li>
                      <li className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" /> {t("free_plan_manual") || "5 manual library prompts"}</li>
                      <li className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" /> {t("free_plan_list") || "1 custom list"}</li>
                      <li className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" /> {t("free_plan_history") || "Full history & favorites"}</li>
                    </ul>
                  </div>
                </div>
                <Link to={user ? "/app" : "/signup"}>
                  <Button data-testid="plan-cta-free" className="rounded-full h-11 px-6 bg-emerald-500 hover:bg-emerald-600 text-black font-black">
                    {user ? (t("open_app") || "Open app") : (t("start_free") || "Start free")}
                  </Button>
                </Link>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {["basic","plus","pro"].map((key) => {
                const p = plans[key];
                const monthly = p.monthly_price;
                const annualEquivalent = p.yearly_price / 12; // discounted monthly equivalent
                const displayPrice = billing === "monthly" ? monthly : annualEquivalent;
                const showStrike = billing === "yearly" && monthly > 0;
                const isFeatured = key === "plus";
                return (
                  <div key={key} className={`rounded-lg p-8 flex flex-col ${isFeatured ? "border-2 border-orange-500 bg-orange-500/[0.03]" : "border border-white/10 bg-white/[0.02]"}`} data-testid={`plan-card-${key}`}>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2 text-orange-400">{p.name}</div>
                    {key === "basic" && (
                      <div className="inline-flex self-start items-center gap-1.5 text-[10px] font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-1 mb-3" data-testid="moneyback-badge">
                        <ShieldCheck className="w-3 h-3" /> {t("moneyback_14d") || "14-day money-back"}
                      </div>
                    )}
                    <div className="mb-2 flex items-baseline flex-wrap gap-2">
                      {showStrike && (
                        <span className="text-2xl text-red-400/70 line-through decoration-red-400/70 decoration-2 font-bold tracking-tight" data-testid={`strike-${key}`}>
                          €{monthly.toFixed(2)}
                        </span>
                      )}
                      <span className="text-5xl font-black tracking-tight">
                        {displayPrice.toFixed(2)}
                      </span>
                      <span className="text-zinc-500">€ / {t("per_month").replace("/ ", "")}</span>
                    </div>
                    {billing === "yearly" && (
                      <div className="text-xs text-zinc-500 mb-6" data-testid={`billed-note-${key}`}>
                        {t("billed_annually_note")} · €{p.yearly_price.toFixed(2)}
                      </div>
                    )}
                    {billing !== "yearly" && <div className="mb-6" />}
                    <ul className="space-y-3 mb-8 flex-1">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                          <Check className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={user ? `/app/account?upgrade=${key}&billing=${billing}` : `/signup?plan=${key}&billing=${billing}`}>
                      <Button
                        data-testid={`plan-cta-${key}`}
                        className={`w-full rounded-full h-11 font-bold ${isFeatured ? "bg-orange-500 hover:bg-orange-600 text-black" : "bg-white/10 hover:bg-white/15 text-white border border-white/10"}`}
                      >
                        {t("activate")}
                      </Button>
                    </Link>
                  </div>
                );
              })}
              </div>
            </>
          )}
          <PaymentIcons />
        </div>
      </section>

      <section id="faq" className="py-24 px-6 border-t border-white/10">
        <div className="max-w-3xl mx-auto">
          <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-3">{t("faq_label")}</div>
          <h2 className="font-black text-4xl md:text-5xl tracking-tight mb-10">{t("faq_title")}</h2>
          <Accordion type="single" collapsible className="w-full">
            {FAQ_KEYS.map((k, i) => (
              <AccordionItem key={k} value={`i-${i}`} className="border-white/10" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-white hover:text-orange-400">{faqDict[k][0]}</AccordionTrigger>
                <AccordionContent className="text-zinc-400 leading-relaxed">{faqDict[k][1]}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="px-6 py-20 bg-[#EADFC9]">
        <div className="max-w-5xl mx-auto rounded-3xl bg-[#14141A] overflow-hidden relative">
          <img src={ABSTRACT_ORANGE} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#14141A] via-[#14141A]/90 to-transparent" />
          <div className="relative p-10 md:p-14 max-w-xl">
            <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-3">Vixelforge</div>
            <h2 className="font-black text-3xl md:text-4xl tracking-tight text-[#F4EFEB] mb-3">{t("cta_band_title") || "Smetti di riscrivere. Inizia a creare."}</h2>
            <p className="text-zinc-300 mb-7">{t("cta_band_sub") || "Un brief, un prompt pronto per il modello che scegli. Prova gratis, senza carta."}</p>
            <Link to={user ? "/app" : "/signup"}>
              <Button size="lg" data-testid="cta-band-btn" className="bg-orange-500 hover:bg-orange-600 text-black font-bold rounded-full px-8 h-12 shadow-[0_0_40px_-10px_rgba(249,115,22,0.6)]">
                {user ? (t("open_app") || "Apri l'app") : t("try_free")} <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-12 px-6 bg-[#14141A]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-6 text-sm text-zinc-500">
          <div className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="Vixelforge" className="w-6 h-6 shrink-0" />
            <span className="font-bold text-white">Vixelforge</span>
            <span>© 2026</span>
          </div>
          <div className="flex gap-6">
            <Link to="/support" className="hover:text-white" data-testid="landing-support-link">{t("support")}</Link>
            <Link to="/privacy" className="hover:text-white">{t("privacy")}</Link>
            <Link to="/cookie" className="hover:text-white">{t("cookie")}</Link>
            <Link to="/terms" className="hover:text-white">{t("terms")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
